import React, { useState } from "react";
import { AlertTriangle, ChevronRight, Clock, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { AiStepTiming, AiToolTraceEntry, AiTurnTiming, AiUsageSummary } from "../../../models/AiAssistant";

interface AiToolTraceProps {
	trace: AiToolTraceEntry[];
	usage?: AiUsageSummary;
	timing?: AiTurnTiming;
	className?: string;
}

/**
 * Collapsed "N verktygsanrop" row under an assistant turn. Expands to the
 * tool name, the input the model chose and a preview of what came back —
 * the same trace the dev CLI prints, so a user can see *why* an answer looks
 * the way it does. When the turn carries timing, the calls are grouped per
 * model step with the model time and each tool's own wall time, so a slow
 * answer can be traced to the model, a tool or the number of steps.
 */
const AiToolTrace: React.FC<AiToolTraceProps> = ({ trace, usage, timing, className }) => {
	const [open, setOpen] = useState(false);

	if (!trace.length) return null;

	const failed = trace.filter((entry) => entry.error).length;
	const steps = timing?.steps ?? [];
	const unstamped = trace.filter((entry) => entry.step === undefined || !steps.some((step) => step.step === entry.step));

	return (
		<div className={cn("text-xs", className)}>
			<button
				type="button"
				onClick={() => setOpen((value) => !value)}
				className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-muted-foreground hover:bg-neutral-100 hover:text-foreground"
			>
				<ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />
				<Wrench className="h-3 w-3" />
				<span>
					{trace.length} {trace.length === 1 ? "verktygsanrop" : "verktygsanrop"}
				</span>
				{failed > 0 && (
					<span className="inline-flex items-center gap-0.5 text-amber-700">
						<AlertTriangle className="h-3 w-3" /> {failed} fel
					</span>
				)}
				{usage && (
					<span className="text-foreground-subtle">
						· {usage.steps} steg · {formatTokens(usage.inputTokens + usage.outputTokens)} tokens
					</span>
				)}
				{timing && (
					<span className="inline-flex items-center gap-0.5 text-foreground-subtle" title={timingTitle(timing)}>
						· <Clock className="h-3 w-3" /> {formatMs(timing.totalMs)}
					</span>
				)}
			</button>

			{open && (
				<div className="mt-1 space-y-2 border-l border-border pl-3">
					{steps.map((step) => (
						<StepGroup key={step.step} step={step} entries={trace.filter((entry) => entry.step === step.step)} />
					))}
					{unstamped.length > 0 && (
						<ol className="space-y-1">
							{unstamped.map((entry, index) => (
								<TraceEntry key={index} entry={entry} />
							))}
						</ol>
					)}
				</div>
			)}
		</div>
	);
};

/** One model step: its own time line, then the tool calls it made. */
const StepGroup: React.FC<{ step: AiStepTiming; entries: AiToolTraceEntry[] }> = ({ step, entries }) => (
	<div>
		<div className="flex flex-wrap items-center gap-x-2 text-[11px] text-foreground-subtle">
			<span className="font-medium text-muted-foreground">Steg {step.step}</span>
			<span>modell {formatMs(step.modelMs)}</span>
			<span>
				{formatTokens(step.inputTokens)} in / {formatTokens(step.outputTokens)} ut
			</span>
			{entries.length === 0 && <span>{step.toolCalls.length ? "väntar på godkännande" : "svar"}</span>}
		</div>
		{entries.length > 0 && (
			<ol className="mt-1 space-y-1">
				{entries.map((entry, index) => (
					<TraceEntry key={index} entry={entry} />
				))}
			</ol>
		)}
	</div>
);

const TraceEntry: React.FC<{ entry: AiToolTraceEntry }> = ({ entry }) => (
	<li className="rounded-md bg-neutral-50 p-2">
		<div className="flex items-center gap-1 font-mono font-medium">
			{entry.toolName}
			{entry.error && (
				<span className="ml-1 rounded bg-amber-100 px-1 font-sans text-[11px] text-amber-800">{entry.error}</span>
			)}
			{entry.durationMs !== undefined && (
				<span className="ml-auto font-sans text-[11px] font-normal tabular-nums text-foreground-subtle">
					{formatMs(entry.durationMs)}
				</span>
			)}
		</div>
		<pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px] text-muted-foreground">
			{JSON.stringify(entry.input)}
		</pre>
		<pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] text-foreground-subtle">
			{entry.outputPreview}
		</pre>
	</li>
);

function formatTokens(tokens: number) {
	return tokens >= 1000 ? `${(tokens / 1000).toFixed(1)}k` : String(tokens);
}

function formatMs(ms: number) {
	if (ms < 1000) return `${Math.round(ms)} ms`;
	return `${(ms / 1000).toFixed(1).replace(".", ",")} s`;
}

function timingTitle(timing: AiTurnTiming) {
	return `Totalt ${formatMs(timing.totalMs)} · modell ${formatMs(timing.modelMs)} · verktyg ${formatMs(timing.toolMs)}`;
}

export default AiToolTrace;
